import React, { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { useNavigate, useParams } from "react-router-dom";
import { auth, calls as callsApi, surveys as surveysApi } from "../api/endpoints";
import type { Call, CallSurveyInfo, ExistingSurveyResponse, Survey } from "../types";

const ADMIN_ROLES = ["SuperAdmin", "Admin"];

interface QuestionChoice {
  id: number;
  point_value_id: number;
  point_value_name: string;
  point_value_score: number | null;
}

function htmlToPlainText(html: string) {
  if (!html) return "";
  const temp = document.createElement("div");
  temp.innerHTML = html;
  return (temp.textContent || temp.innerText || "").trim();
}

export default function SurveyPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [call, setCall] = useState<Call | null>(null);
  const [surveyInfo, setSurveyInfo] = useState<CallSurveyInfo | null>(null);
  const [templates, setTemplates] = useState<{ id: number; name: string }[]>([]);
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [existingResponse, setExistingResponse] = useState<ExistingSurveyResponse | null>(null);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [comments, setComments] = useState<Record<number, string>>({});
  const [questionChoices, setQuestionChoices] = useState<Record<number, QuestionChoice[]>>({});
  const [submitting, setSubmitting] = useState(false);
  const [savingComments, setSavingComments] = useState(false);
  const [loadingExistingResponse, setLoadingExistingResponse] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [showManualPicker, setShowManualPicker] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Load call details and auto-resolve survey from contract spec
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    const callId = Number(id);

    const hydrate = async () => {
      try {
        const [loadedCall, loadedTemplates] = await Promise.all([
          callsApi.get(callId),
          surveysApi.templates(),
        ]);
        if (cancelled) return;
        setCall(loadedCall);
        setTemplates(loadedTemplates);

        auth.me()
          .then((u) => {
            if (!cancelled) setIsAdmin(ADMIN_ROLES.includes(u.role?.name || ""));
          })
          .catch(() => {});

        const info = await surveysApi.forCall(callId);
        if (cancelled) return;
        setSurveyInfo(info);

        if (info.has_existing_response) {
          setLoadingExistingResponse(true);
          try {
            const existing = await surveysApi.existingResponse(callId);
            if (cancelled) return;
            setExistingResponse(existing);
            await loadSurvey(existing.survey_id, existing);
          } finally {
            if (!cancelled) setLoadingExistingResponse(false);
          }
          return;
        }

        setExistingResponse(null);
        if (info.survey_id) {
          await loadSurvey(info.survey_id);
        }
      } catch (err: any) {
        if (!cancelled) {
          const msg = err?.response?.data?.detail || err?.message || "Unable to load survey context";
          setError(typeof msg === "string" ? msg : JSON.stringify(msg));
        }
      }
    };

    hydrate();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const loadSurvey = async (surveyId: number, existing?: ExistingSurveyResponse | null) => {
    setError("");
    const nextSurvey = await surveysApi.getTemplate(surveyId);
    setSurvey(nextSurvey);
    setShowManualPicker(false);

    const initAnswers: Record<number, number> = {};
    const initComments: Record<number, string> = {};
    nextSurvey.categories.forEach((category) => {
      category.questions.forEach((question) => {
        initAnswers[question.id] = 0;
        initComments[question.id] = "";
      });
    });
    if (existing) {
      existing.radio_answers.forEach((answer) => {
        initAnswers[answer.question_id] = answer.score;
      });
      existing.comments.forEach((comment) => {
        initComments[comment.question_id] = comment.text;
      });
    }
    setAnswers(initAnswers);
    setComments(initComments);

    // Load choices for each category's questions
    const choicesMap: Record<number, QuestionChoice[]> = {};
    for (const cat of nextSurvey.categories) {
      try {
        const questions = await surveysApi.questionsByCategory(cat.id);
        for (const q of questions) {
          if (q.choices && q.choices.length > 0) {
            choicesMap[q.id] = q.choices;
          }
        }
      } catch {
        // If endpoint fails, fall back to slider mode
      }
    }
    setQuestionChoices(choicesMap);
  };

  const handleSubmit = async () => {
    if (!call || !survey) return;
    setError("");
    setNotice("");
    setSubmitting(true);
    try {
      const radioAnswers = Object.entries(answers).map(([questionId, score]) => ({
        question_id: Number(questionId),
        score,
      }));
      const commentList = buildCommentList();

      const payload = {
        survey_id: survey.id,
        radio_answers: radioAnswers,
        comments: commentList,
      };
      if (existingResponse) {
        await surveysApi.update(call.id, payload);
      } else {
        await surveysApi.submit(call.id, payload);
      }
      navigate(`/calls/${call.id}`);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || "Survey submission failed";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setSubmitting(false);
    }
  };

  const buildCommentList = () => {
    const visibleQuestionIds = new Set(
      survey?.categories.flatMap((category) => category.questions.map((question) => question.id)) || []
    );
    return Object.entries(comments)
      .filter(([questionId]) => visibleQuestionIds.has(Number(questionId)))
      .filter(([, text]) => text.trim())
      .map(([questionId, text]) => ({ question_id: Number(questionId), text }));
  };

  const handleSaveComments = async () => {
    if (!call || !survey) return;
    setError("");
    setNotice("");
    setSavingComments(true);
    try {
      const saved = await surveysApi.saveComments(call.id, {
        survey_id: survey.id,
        comments: buildCommentList(),
      });
      setExistingResponse(saved);
      setNotice("Review comments saved.");
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || "Comment save failed";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setSavingComments(false);
    }
  };

  if (!call) {
    return (
      <div className="surface-panel px-6 py-10 text-center">
        <div className="eyebrow">Survey workspace</div>
        <p className="mt-3 text-lg text-slate-600">Loading survey context...</p>
      </div>
    );
  }

  if (loadingExistingResponse && !survey) {
    return (
      <div className="surface-panel px-6 py-10 text-center">
        <div className="eyebrow">Survey workspace</div>
        <p className="mt-3 text-lg text-slate-600">Loading saved survey response...</p>
      </div>
    );
  }

  const questionCount = survey
    ? survey.categories.reduce((count, category) => count + category.questions.length, 0)
    : 0;
  const answeredCount = survey
    ? Object.values(answers).filter((score) => score > 0).length
    : 0;
  const totalScore = Object.values(answers).reduce((sum, score) => sum + score, 0);
  const maxPossible = survey
    ? survey.categories.reduce(
        (sum, cat) => sum + cat.questions.reduce((qs, q) => qs + q.max_score, 0),
        0
      )
    : 0;
  const percentAchieved = maxPossible > 0 ? Math.min(Math.round((totalScore / maxPossible) * 100), 100) : 0;
  const isEditingExisting = Boolean(existingResponse);
  const canChangeTemplate = isAdmin && !isEditingExisting;

  return (
    <div className="space-y-6">
      {/* Header with live scoring stats */}
      <section className="surface-hero rise-in px-6 py-7 md:px-8">
        <div className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <button onClick={() => navigate(`/calls/${call.id}`)} className="button-ghost !px-4 !py-2 text-sm">
              Back to Call
            </button>
            <div className="mt-5">
              <div className="eyebrow">Survey capture</div>
              <h1 className="page-title">Call #{call.id} review</h1>
              <p className="page-copy">
                {call.property_name} ({call.property_hotel_code}) for {call.service_level.name} service coverage.
              </p>
              {surveyInfo?.management_company_name && (
                <p className="mt-1 text-sm text-slate-500">
                  HMC: <span className="font-semibold text-slate-700">{surveyInfo.management_company_name}</span>
                  {surveyInfo.survey_name && (
                    <>
                      {" "}— Auto-resolved survey:{" "}
                      <span className="font-semibold text-teal-700">{surveyInfo.survey_name}</span>
                    </>
                  )}
                </p>
              )}
              {isEditingExisting && (
                <p className="mt-2 text-sm font-medium text-amber-700">
                  {existingResponse?.is_completed
                    ? "Editing an existing submitted survey. Saving here will recalculate the call score and report totals."
                    : "Review comments are saved as a draft. Submitting the full survey will calculate the call score."}
                </p>
              )}
              {error && (
                <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700">
                  {error}
                </div>
              )}
              {notice && (
                <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
                  {notice}
                </div>
              )}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-4">
            <div className="surface-muted p-4">
              <div className="label-caps">Template</div>
              <div className="mt-2 text-lg font-semibold tracking-tight">{survey?.name || "Select one"}</div>
            </div>
            <div className="surface-muted p-4">
              <div className="label-caps">Answered</div>
              <div className="mt-2 text-lg font-semibold tracking-tight">
                {answeredCount}/{questionCount}
              </div>
            </div>
            <div className="surface-muted p-4">
              <div className="label-caps">Score</div>
              <div className="mt-2 text-lg font-semibold tracking-tight">
                {totalScore}/{maxPossible}
              </div>
            </div>
            <div className="surface-muted p-4">
              <div className="label-caps">Percentage</div>
              <div className={`mt-2 text-lg font-semibold tracking-tight ${
                percentAchieved >= 70 ? "text-emerald-700" : percentAchieved >= 50 ? "text-amber-700" : "text-rose-700"
              }`}>
                {percentAchieved}%
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call Details — mirrors legacy survey header */}
      <section className="surface-panel rise-in delay-1 p-6 md:p-7">
        <h2 className="text-lg font-semibold mb-4">Call Details</h2>
        <div className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
          <div className="flex items-baseline gap-2">
            <span className="text-sm text-slate-500 min-w-[10rem]">Hotel identifier:</span>
            <span className="text-sm font-medium">{call.property_hotel_code || "—"}</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-sm text-slate-500 min-w-[10rem]">Property name:</span>
            <span className="text-sm font-medium">{call.property_name || "—"}</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-sm text-slate-500 min-w-[10rem]">Sales staff:</span>
            <span className="text-sm font-medium">{call.sales_staff_name || "—"}</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-sm text-slate-500 min-w-[10rem]">Shopper:</span>
            <span className="text-sm font-medium">{call.shopper_name || "—"}</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-sm text-slate-500 min-w-[10rem]">Call type:</span>
            <span className="text-sm font-medium">{call.call_type?.name || "—"}</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-sm text-slate-500 min-w-[10rem]">Service level:</span>
            <span className="text-sm font-medium">{call.service_level?.name || "—"}</span>
          </div>
          {call.scenario && (
            <div className="sm:col-span-2 flex items-start gap-2">
              <span className="text-sm text-slate-500 min-w-[10rem]">Scenario:</span>
              <span className="text-sm font-medium whitespace-pre-wrap">{call.scenario}</span>
            </div>
          )}
        </div>
      </section>

      {/* Error banner */}
      {error && (
        <div className="surface-panel border-l-4 border-rose-500 p-4 text-sm text-rose-800">
          <strong>Error:</strong> {error}
          <button onClick={() => setError("")} className="ml-4 underline">Dismiss</button>
        </div>
      )}

      {/* Survey template selection — only shown if not auto-resolved or user wants to change */}
      {!survey || (showManualPicker && !isEditingExisting) ? (
        <section className="surface-panel rise-in delay-1 p-6 md:p-7">
          <div className="eyebrow">Step 1</div>
          <h2 className="mt-2 text-2xl font-semibold tracking-tight">Choose a survey template</h2>
          {surveyInfo?.survey_id && (
            <p className="mt-2 text-sm text-teal-700">
              The contract for this HMC auto-resolved to <strong>{surveyInfo.survey_name}</strong>. You can override by
              selecting a different template below.
            </p>
          )}
          {!surveyInfo?.survey_id && (
            <p className="mt-2 text-sm text-amber-700">
              No survey is configured in the contract specification for this property's HMC. Please select a template
              manually.
            </p>
          )}
          <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {templates.map((template) => {
              const isAutoResolved = template.id === surveyInfo?.survey_id;
              return (
                <button
                  key={template.id}
                  onClick={() => loadSurvey(template.id)}
                  className={`surface-muted p-5 text-left transition hover:-translate-y-1 ${
                    isAutoResolved ? "ring-2 ring-teal-400" : ""
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className="label-caps">Template</div>
                    {isAutoResolved && (
                      <span className="badge-pill bg-teal-100 text-teal-800 text-xs">Contract default</span>
                    )}
                  </div>
                  <div className="mt-3 text-xl font-semibold tracking-tight">{template.name}</div>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    Load this question set and begin scoring the call immediately.
                  </p>
                </button>
              );
            })}
          </div>
        </section>
      ) : (
        <>
          {/* Survey answering section */}
          <section className="surface-panel rise-in delay-1 p-6 md:p-7">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="eyebrow">Step 2</div>
                <h2 className="mt-2 text-2xl font-semibold tracking-tight">{survey.name}</h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  {survey.description || "Score each question and add comments where context matters."}
                </p>
                {isEditingExisting && existingResponse && (
                  <p className="mt-2 text-sm text-slate-500">
                    Last updated {new Date(existingResponse.updated_at).toLocaleString()}.
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                {canChangeTemplate && (
                  <button onClick={() => setShowManualPicker(true)} className="button-ghost text-sm">
                    Change Template
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleSaveComments}
                  disabled={savingComments || submitting}
                  className="button-ghost text-sm"
                >
                  {savingComments ? "Saving Comments..." : "Save Comments"}
                </button>
                <div className="w-full max-w-sm">
                  <div className="flex items-center justify-between text-sm text-slate-600">
                    <span>Completion</span>
                    <span>{questionCount ? Math.round((answeredCount / questionCount) * 100) : 0}%</span>
                  </div>
                  <div className="progress-track mt-2">
                    <div
                      className="progress-fill"
                      style={{
                        width: `${questionCount ? Math.round((answeredCount / questionCount) * 100) : 0}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="space-y-5">
            {survey.categories.map((category, categoryIndex) => (
              <div key={category.id} className="surface-panel rise-in p-6 md:p-7">
                <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                  <div>
                    <div className="eyebrow">Category {categoryIndex + 1}</div>
                    <h3 className="mt-2 text-2xl font-semibold tracking-tight">{category.name}</h3>
                  </div>
                  <div className="text-sm text-slate-500">{category.questions.length} questions</div>
                </div>

                <div className="mt-6 space-y-4">
                  {category.questions.map((question) => {
                    const currentScore = answers[question.id] || 0;
                    const choices = questionChoices[question.id];
                    const hasChoices = choices && choices.length > 0;
                    const effectiveMax = question.max_score;
                    return (
                      <div key={question.id} className="surface-muted p-5">
                        <div className="flex items-start gap-3">
                          <span className="text-sm text-slate-400 font-mono pt-0.5 min-w-[2rem] text-right shrink-0">{question.order}.</span>
                          <div className="flex-1 min-w-0">
                            <span className="text-rose-500 font-bold">*</span>
                            <div
                              className="mt-1 text-base leading-7 text-slate-800 question-html-content"
                              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(question.text || "") }}
                            />
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            <span className={`badge-pill ${
                              currentScore > 0 ? "bg-emerald-100 text-emerald-800" :
                              currentScore < 0 ? "bg-amber-100 text-amber-800" :
                              "bg-slate-100 text-slate-700"
                            }`}>
                              {currentScore} pts
                            </span>
                          </div>
                        </div>

                        <div className="mt-4 ml-8">
                          {hasChoices ? (
                            /* Radio-style choices matching legacy format */
                            <div className="space-y-2">
                              {choices
                                .sort((a, b) => (b.point_value_score ?? 0) - (a.point_value_score ?? 0))
                                .map((choice) => {
                                const score = choice.point_value_score ?? 0;
                                const isSelected = currentScore === score;
                                return (
                                  <label
                                    key={choice.id}
                                    className={`flex items-center gap-3 cursor-pointer rounded-lg px-3 py-2 transition-colors ${
                                      isSelected
                                        ? "bg-teal-50 border border-teal-300"
                                        : "hover:bg-slate-50 border border-transparent"
                                    }`}
                                  >
                                    <input
                                      type="radio"
                                      name={`q_${question.id}`}
                                      checked={isSelected}
                                      onChange={() =>
                                        setAnswers({ ...answers, [question.id]: score })
                                      }
                                      className="h-4 w-4 text-teal-600 border-slate-300 focus:ring-teal-500"
                                    />
                                    <span className="text-sm font-medium">{choice.point_value_name}</span>
                                    <span className={`text-xs font-mono ${
                                      score > 0 ? "text-emerald-600" : score < 0 ? "text-red-600" : "text-slate-400"
                                    }`}>
                                      {score}
                                    </span>
                                  </label>
                                );
                              })}
                            </div>
                          ) : (
                            /* Fallback slider for questions without defined choices */
                            <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
                              <div className="flex-1">
                                <input
                                  type="range"
                                  min={0}
                                  max={effectiveMax || 10}
                                  step={0.5}
                                  value={currentScore}
                                  onChange={(e) =>
                                    setAnswers({ ...answers, [question.id]: Number(e.target.value) })
                                  }
                                  className="survey-range"
                                />
                              </div>
                              <div className="badge-pill bg-teal-900 text-white">{currentScore}</div>
                            </div>
                          )}

                          <div className="mt-4">
                            {question.comment_prompt && (
                              <div className="mb-3 rounded-xl border border-dashed border-teal-200 bg-teal-50/70 p-3">
                                <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                                  <div className="min-w-0">
                                    <div className="text-xs font-semibold uppercase tracking-[0.24em] text-teal-700">
                                      Suggested comment
                                    </div>
                                    <div
                                      className="mt-2 text-sm leading-6 text-slate-700"
                                      dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(question.comment_prompt),
                                      }}
                                    />
                                  </div>
                                  <div className="flex shrink-0 gap-2">
                                    <button
                                      type="button"
                                      className="button-ghost !px-3 !py-2 text-xs"
                                      onClick={() =>
                                        setComments({
                                          ...comments,
                                          [question.id]: comments[question.id]
                                            ? `${comments[question.id].trim()}\n${htmlToPlainText(question.comment_prompt)}`
                                            : htmlToPlainText(question.comment_prompt),
                                        })
                                      }
                                    >
                                      Insert
                                    </button>
                                    <button
                                      type="button"
                                      className="button-ghost !px-3 !py-2 text-xs"
                                      onClick={async () => {
                                        await navigator.clipboard.writeText(htmlToPlainText(question.comment_prompt));
                                      }}
                                    >
                                      Copy
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}
                            <label className="text-sm text-slate-500 block mb-1">Please enter your comment here:</label>
                            <textarea
                              value={comments[question.id] || ""}
                              onChange={(e) => setComments({ ...comments, [question.id]: e.target.value })}
                              className="textarea-shell w-full min-h-[80px]"
                              rows={3}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </section>

          {/* Submit section with live score preview */}
          <section className="surface-panel rise-in delay-2 p-6 md:p-7">
            <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="eyebrow">Final step</div>
                <h2 className="mt-2 text-2xl font-semibold tracking-tight">Submit survey</h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  Review the score distribution and submit when the call evaluation is complete.
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <div className="badge-pill bg-slate-100 text-slate-700">
                    Score: {totalScore}/{maxPossible}
                  </div>
                  <div className={`badge-pill ${
                    percentAchieved >= 70 ? "bg-emerald-100 text-emerald-800" :
                    percentAchieved >= 50 ? "bg-amber-100 text-amber-800" :
                    "bg-rose-100 text-rose-800"
                  }`}>
                    {percentAchieved}%
                  </div>
                  <div className="badge-pill bg-slate-100 text-slate-700">
                    {answeredCount}/{questionCount} answered
                  </div>
                  {answeredCount < questionCount && (
                    <div className="badge-pill bg-amber-100 text-amber-800">
                      {questionCount - answeredCount} unanswered
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                {canChangeTemplate && (
                  <button onClick={() => setShowManualPicker(true)} className="button-ghost">
                    Change Template
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleSaveComments}
                  disabled={savingComments || submitting}
                  className="button-ghost"
                >
                  {savingComments ? "Saving Comments..." : "Save Comments"}
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting || savingComments || answeredCount === 0}
                  className="button-primary"
                >
                  {submitting
                    ? (existingResponse ? "Saving..." : "Submitting...")
                    : (existingResponse ? "Save Survey Changes" : "Submit Survey")}
                </button>
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
